<template>
  <div id="layout">
    <el-config-provider :locale="datepickerLanguage">
      <experimental-bar />
      <main-header />
      <!-- Todo: Add route validation to a utility when applying it to more pages. -->
      <main class="container" :class="{ 'responsive-container': shouldUseResponsiveContainer }">
        <router-view></router-view>

        <transition name="fade">
          <div v-show="!isDialogVisible && insightsCount > 0" class="alert">
            <div v-if="!hasUserViewedOtoInsightsToday" class="alert__content">
              <p class="alert__text">{{ $t('strategic-insights.floating-alert.description') }}</p>
              <p class="alert__text--highlight">
                {{ $tc('strategic-insights.floating-alert.highlighted', insightsCount, { insightsCount }) }}
              </p>
            </div>

            <div
              v-if="!hasUserViewedOtoInsightsToday"
              ref="floatingAlert"
              class="alert__icon"
              @click="handleInsightsIconClick"
            />
            <img
              v-else
              class="alert__icon--img"
              :src="require(`@/assets/svgs/campaign-oto-static.svg`)"
              @click="handleInsightsIconClick"
            />
          </div>
        </transition>
      </main>

      <footer-content />
    </el-config-provider>

    <insights-container-modal />
  </div>
</template>

<script>
import moment from 'moment';
import FooterContent from '@/components/FooterContent';
import MainHeader from '@/components/menu/TheHeader';
import ExperimentalBar from '@/components/menu/TheExperimentalBar';
import InsightsContainerModal from '@/features/OtoInsightsModal/components/InsightsContainerModal.vue';

import { datepickerLanguage } from '@/helpers/setup-datepicker';
import { ref, computed, onMounted } from 'vue';
import { useLottie } from '@/composables/useLottie';
import { useCampaignsInsights, useInsightsModalContextProvider } from '@/features/OtoInsightsModal/composables';
import campaignOtoPulse from '@/assets/json/campaign-oto-pulse.json';
import { useMixpanelConfig } from '@/utilities/mixpanel';

export default {
  name: 'Layout',
  components: {
    MainHeader,
    ExperimentalBar,
    FooterContent,
    InsightsContainerModal,
  },
  setup() {
    // Dialog
    const isDialogVisible = ref(false);

    function toggleDialogVisibility() {
      isDialogVisible.value = !isDialogVisible.value;
    }

    useInsightsModalContextProvider({ isDialogVisible, toggleDialogVisibility });

    // Insights floating alert
    const { insights, hasUserViewedOtoInsightsToday, loadCampaignsInsights, handleUserViewedOtoInsights } =
      useCampaignsInsights();

    const floatingAlert = ref(null);
    const insightsCount = computed(() => {
      if (!insights.value) return 0;
      return Object.values(insights.value).flat().length;
    });

    const {
      animationInstance,
      loadAnimation: loadCampaignOtoPulseAnimation,
      pauseAtFrameAndResume,
    } = useLottie(floatingAlert, {
      loop: true,
      autoplay: true,
      animationData: campaignOtoPulse,
    });

    onMounted(() => {
      loadCampaignsInsights();
      loadCampaignOtoPulseAnimation();

      if (animationInstance.value) {
        const pauseAtFrame = Math.ceil(animationInstance.value.getDuration(true) / 2);
        const pauseDurationMs = 2000;

        pauseAtFrameAndResume(pauseAtFrame, pauseDurationMs);
      }
    });

    return {
      // Dialog
      isDialogVisible,
      toggleDialogVisibility,
      // Insights floating alert
      insightsCount,
      floatingAlert,
      hasUserViewedOtoInsightsToday,
      handleUserViewedOtoInsights,
    };
  },
  methods: {
    openPriorityEditConfirmationModalAction(next) {
      if (this.isDataActionsUnchanged) {
        next();
      } else {
        this.$store.dispatch('campaignsManagement/setOpenConfirmEditPriorityModal', true);
        next(false);
      }
    },
    handleInsightsIconClick() {
      useMixpanelConfig('view_oto_campaigns_insights');

      this.handleUserViewedOtoInsights();
      this.toggleDialogVisibility();
    },
  },
  computed: {
    datepickerLanguage() {
      return datepickerLanguage(this.$store?.getters?.user?.language);
    },
    getRoute() {
      return this.$route.name;
    },
    shouldUseResponsiveContainer() {
      const responsiveRoutes = ['campaign-campaigns', 'campaign-sms', 'email-campaign-list'];
      return responsiveRoutes.includes(this.getRoute);
    },

    isDataActionsUnchanged() {
      return this.$store.state.campaignsManagement.isDataUnchanged;
    },
  },
  created() {
    const lang = this.$store.getters.user?.language;
    this.$i18n.locale = lang;
    moment.locale(lang);
  },
  beforeRouteUpdate(_to, from, next) {
    if (from.name === 'campaign-campaigns') {
      this.openPriorityEditConfirmationModalAction(next);
    } else {
      next();
    }
  },

  beforeRouteLeave(_to, from, next) {
    if (from.name === 'campaign-campaigns') {
      this.openPriorityEditConfirmationModalAction(next);
    } else {
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding-top: 30px;
  height: 100%;
  min-height: 100vh;
}

.responsive-container {
  margin-right: 0;
  margin-left: 98px;
  max-width: 2416px;
  width: calc(100% - 120px);

  @media (min-width: 1236px) and (max-width: 2416px) {
    min-width: 1136px;
    width: calc(100% - 130px);
  }

  @media (min-width: 2417px) {
    max-width: 2416px;
    width: calc(100% - 300px);
    margin: auto;
  }
}

.alert {
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  position: fixed;
  z-index: 9999;
  bottom: 0px;
  right: 10px;

  &__content {
    width: 320px;
    margin-bottom: 10px;
    background-color: $gray-100;
    padding: 16px 20px;
    border-radius: 10px;
    border: 1px solid $gray-200;
    box-shadow: 0px 6px 20px 0px rgba(0, 0, 0, 0.15);
    display: grid;
    gap: 6px;

    p {
      margin: 0;
    }
  }

  &__text {
    margin: 0 0 5px 0;
    font-weight: 500;
    font-size: 13px;

    &--highlight {
      font-size: 13px;
      font-weight: 700;
      color: $pink-500;
    }
  }

  &__icon {
    width: 100px;
    cursor: pointer;

    &--img {
      width: 60px;
      cursor: pointer;
      border-radius: 100%;
      box-shadow: 0px 6px 20px 0px rgba(0, 0, 0, 0.15);
    }
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease, transform 0.5s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
